import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Mail, MessageSquare, PauseCircle, X } from 'lucide-react';
import {
  MONATSNAMEN, VERSAND_INFO, WOCHENTAGE_KURZ, alsTag, datumZeit, monatTitel, plusTage, rueckmeldungHinweis, tagIso, wochenStart,
  zeitraumText, type PlanungsMail,
} from '../../lib/planung';
import './Planung.css';

type Props = {
  mails: PlanungsMail[];
  /** irgendein Tag im gezeigten Monat */
  monat: Date;
  onMonat: (d: Date) => void;
  onOeffnen: (m: PlanungsMail) => void;
  heute?: Date;
  /** Was der Kalender zeigt: Versandtage und Events (Standard), nur Versandtage oder nur Events. */
  zeige?: KalenderZeige;
  /** Team: Mails mit offenen oder neuen Rückmeldungen farbig hervorheben (Sprechblase mit Zahl). */
  rueckmeldungen?: boolean;
};

export type KalenderZeige = 'beide' | 'versand' | 'event';

/** Event-Balken je Woche, die sichtbar übereinander liegen dürfen. */
const MAX_SPUREN = 2;
/** Einträge je Tag (Balken und Versand), bevor „+N mehr" erscheint. */
const MAX_JE_TAG = 3;
const WOCHENTAG_LANG = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/** Teil eines Events innerhalb einer Kalenderwoche. */
type Abschnitt = { mail: PlanungsMail; start: number; ende: number; beginntHier: boolean; endetHier: boolean; spur: number };
type Woche = { tage: Date[]; abschnitte: Abschnitt[]; spuren: number };

const tageZwischen = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86_400_000);
const tagLang = (d: Date) => `${WOCHENTAG_LANG[d.getDay()]}, ${d.getDate()}. ${MONATSNAMEN[d.getMonth()]}`;
const titelVon = (m: PlanungsMail) => m.betreff?.trim() || m.name || 'Ohne Betreff';
/** Für „nur Versand“: dieselbe Mail ohne Event-Termin (keine Balken, keine Event-Einträge). */
const ohneEvent = (m: PlanungsMail): PlanungsMail => ({ ...m, event: { ...m.event, beginn: null, ende: null } });
const eventTitel = (m: PlanungsMail) => m.event.titel?.trim() || titelVon(m);
const nurDatum = (wert: string | null) => !!wert && /^\d{4}-\d{2}-\d{2}$/.test(wert);
const uhrzeit = (wert: string | null) =>
  !wert || nurDatum(wert) ? '' : new Date(wert).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

/** „, 2 neue Rückmeldungen" für Beschriftungen — leer, wenn nichts vorliegt oder der Kalender es nicht zeigt. */
function rueckZusatz(m: PlanungsMail, rueck: boolean): string {
  const h = rueck ? rueckmeldungHinweis(m) : null;
  return h ? `, ${h.text}` : '';
}

function markeLabel(m: PlanungsMail, rueck = false): string {
  const art = m.versand.art ? VERSAND_INFO[m.versand.art].label : 'Versand';
  return `${art}: ${titelVon(m)}, ${datumZeit(m.versand.am)}${rueckZusatz(m, rueck)}`;
}

function eventLabel(m: PlanungsMail, rueck = false): string {
  const titel = eventTitel(m);
  const mail = titel === titelVon(m) ? '' : ` – Mail „${titelVon(m)}“`;
  return `Event: ${titel}, ${zeitraumText(m.event.beginn, m.event.ende)}${mail}${rueckZusatz(m, rueck)}`;
}

/** Sprechblase mit Zahl: rot = neu und ungelesen, orange = offen. Das Wort steht in der Beschriftung des Knopfs. */
function RueckBadge({ m, rueck, klein = false }: { m: PlanungsMail; rueck: boolean; klein?: boolean }) {
  const h = rueck ? rueckmeldungHinweis(m) : null;
  if (!h) return null;
  return (
    <span className={`gm-kal-rueck gm-kal-rueck--${h.neu ? 'neu' : 'offen'}${klein ? ' gm-kal-rueck--klein' : ''}`} aria-hidden="true">
      <MessageSquare size={klein ? 9 : 10} />{h.zahl}
    </span>
  );
}

/** Zusatzklasse für Kacheln mit Rückmeldung. */
function rueckKlasse(m: PlanungsMail, rueck: boolean, basis: string): string {
  const h = rueck ? rueckmeldungHinweis(m) : null;
  return h ? ` ${basis}--rueck ${basis}--rueck-${h.neu ? 'neu' : 'offen'}` : '';
}

/** Sechs Wochen ab dem Montag vor dem Monatsersten, Events in Spuren verteilt. */
function baueWochen(monat: Date, mails: PlanungsMail[]): Woche[] {
  const erster = new Date(monat.getFullYear(), monat.getMonth(), 1);
  const start = wochenStart(erster);
  const events = mails
    .map((m) => {
      const a = alsTag(m.event.beginn);
      if (!a) return null;
      const b = alsTag(m.event.ende);
      return { mail: m, a, b: b && b >= a ? b : a };
    })
    .filter((x): x is { mail: PlanungsMail; a: Date; b: Date } => x !== null);

  const wochen: Woche[] = [];
  for (let w = 0; w < 6; w++) {
    const ws = plusTage(start, w * 7);
    const we = plusTage(ws, 6);
    const tage = Array.from({ length: 7 }, (_, i) => plusTage(ws, i));
    const teile = events
      .filter((e) => e.b >= ws && e.a <= we)
      .map((e) => ({
        mail: e.mail,
        start: Math.max(0, tageZwischen(ws, e.a)),
        ende: Math.min(6, tageZwischen(ws, e.b)),
        beginntHier: e.a >= ws,
        endetHier: e.b <= we,
        spur: 0,
      }))
      .sort((x, y) => x.start - y.start || (y.ende - y.start) - (x.ende - x.start) || titelVon(x.mail).localeCompare(titelVon(y.mail), 'de'));
    // Gierig in Spuren legen: erste Spur, deren letzter Tag vor dem Beginn liegt.
    const spurEnde: number[] = [];
    for (const t of teile) {
      let s = spurEnde.findIndex((ende) => ende < t.start);
      if (s === -1) { s = spurEnde.length; spurEnde.push(t.ende); } else spurEnde[s] = t.ende;
      t.spur = s;
    }
    wochen.push({ tage, abschnitte: teile, spuren: Math.min(MAX_SPUREN, spurEnde.length) });
  }
  return wochen;
}

function Marke({ m, onOeffnen, rueck, lang = false }: { m: PlanungsMail; onOeffnen: (m: PlanungsMail) => void; rueck: boolean; lang?: boolean }) {
  const art = m.versand.art ?? 'geplant';
  const Icon = art === 'versendet' ? CheckCircle2 : art === 'angehalten' ? PauseCircle : Mail;
  const zeit = uhrzeit(m.versand.am);
  if (lang) {
    // Liste (Handy, „+N mehr"): Titel ganz, darunter Uhrzeit und Versandart
    return (
      <button type="button" className={`gm-kal-marke gm-kal-marke--${art} gm-kal-marke--lang${rueckKlasse(m, rueck, 'gm-kal-marke')}`} onClick={() => onOeffnen(m)} aria-label={markeLabel(m, rueck)}>
        <Icon size={14} aria-hidden="true" />
        <span className="gm-kal-zeilen">
          <span className="gm-kal-titel-lang">{titelVon(m)}</span>
          <span className="gm-kal-meta">{[zeit, m.versand.art ? VERSAND_INFO[m.versand.art].label : null].filter(Boolean).join(' · ')}</span>
        </span>
        <RueckBadge m={m} rueck={rueck} />
      </button>
    );
  }
  return (
    <button type="button" className={`gm-kal-marke gm-kal-marke--${art}${rueckKlasse(m, rueck, 'gm-kal-marke')}`} onClick={() => onOeffnen(m)} aria-label={markeLabel(m, rueck)} title={markeLabel(m, rueck)}>
      <Icon size={12} aria-hidden="true" />
      {zeit && <span className="gm-kal-zeit">{zeit}</span>}
      <span className="gm-kal-text">{titelVon(m)}</span>
      <RueckBadge m={m} rueck={rueck} />
    </button>
  );
}

function EventEintrag({ m, onOeffnen, rueck }: { m: PlanungsMail; onOeffnen: (m: PlanungsMail) => void; rueck: boolean }) {
  return (
    <button type="button" className={`gm-kal-marke gm-kal-marke--event gm-kal-marke--lang${rueckKlasse(m, rueck, 'gm-kal-marke')}`} onClick={() => onOeffnen(m)} aria-label={eventLabel(m, rueck)}>
      <CalendarDays size={14} aria-hidden="true" />
      <span className="gm-kal-zeilen">
        <span className="gm-kal-titel-lang">{eventTitel(m)}</span>
        <span className="gm-kal-meta">Event · {zeitraumText(m.event.beginn, m.event.ende)}</span>
      </span>
      <RueckBadge m={m} rueck={rueck} />
    </button>
  );
}

/** Liste aller Einträge eines Tages (bei „+N mehr"). */
function TagesPopover({ tag, events, marken, rechts, rueck, onOeffnen, onClose }: {
  tag: Date; events: PlanungsMail[]; marken: PlanungsMail[]; rechts: boolean; rueck: boolean;
  onOeffnen: (m: PlanungsMail) => void; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Beim Öffnen auf den ersten Eintrag, damit es per Tastatur weitergeht.
  useEffect(() => { ref.current?.querySelector<HTMLButtonElement>('.gm-kal-marke')?.focus(); }, []);
  useEffect(() => {
    const taste = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const klick = (e: MouseEvent) => {
      const ziel = e.target as Element;
      // Der „+N mehr"-Knopf schaltet selbst um.
      if (ref.current?.contains(ziel) || ziel.closest?.('.gm-kal-mehr')) return;
      onClose();
    };
    document.addEventListener('keydown', taste);
    document.addEventListener('mousedown', klick);
    return () => {
      document.removeEventListener('keydown', taste);
      document.removeEventListener('mousedown', klick);
    };
  }, [onClose]);
  return (
    <div ref={ref} className={`gm-kal-pop${rechts ? ' gm-kal-pop--rechts' : ''}`} role="dialog" aria-label={`Alles am ${tagLang(tag)}`}>
      <div className="gm-kal-pop-kopf">
        <strong>{tagLang(tag)}</strong>
        <button type="button" className="gm-kal-pop-zu" onClick={onClose} aria-label="Schließen"><X size={14} aria-hidden="true" /></button>
      </div>
      <div className="gm-kal-pop-liste">
        {events.map((m) => <EventEintrag key={`e-${m.id}`} m={m} onOeffnen={onOeffnen} rueck={rueck} />)}
        {marken.map((m) => <Marke key={`v-${m.id}`} m={m} onOeffnen={onOeffnen} rueck={rueck} lang />)}
      </div>
    </div>
  );
}

/**
 * Monatskalender: Versandtage als kleine Briefumschläge, Events als Balken
 * über alle Tage, die sie dauern. Auf dem Handy wird daraus eine Liste der
 * Tage mit Einträgen.
 */
export function PlanungsKalender({ mails: alle, monat, onMonat, onOeffnen, heute, zeige = 'beide', rueckmeldungen = false }: Props) {
  // Nur Versand: keine Event-Balken; nur Events: keine Versandmarken.
  const mails = useMemo(() => (zeige === 'versand' ? alle.map(ohneEvent) : alle), [alle, zeige]);
  const versandMails = useMemo(() => (zeige === 'event' ? [] : alle), [alle, zeige]);
  const heuteTag = alsTag(heute ?? new Date())!;
  const heuteIso = tagIso(heuteTag);
  const jahr = monat.getFullYear();
  const mon = monat.getMonth();
  const [offenerTag, setOffenerTag] = useState<string | null>(null);

  const wochen = useMemo(() => baueWochen(new Date(jahr, mon, 1), mails), [jahr, mon, mails]);

  // Versandtage je Tag, nach Uhrzeit
  const markenJeTag = useMemo(() => {
    const karte = new Map<string, PlanungsMail[]>();
    for (const m of versandMails) {
      const t = alsTag(m.versand.am);
      if (!t) continue;
      const key = tagIso(t);
      if (!karte.has(key)) karte.set(key, []);
      karte.get(key)!.push(m);
    }
    for (const liste of karte.values()) liste.sort((a, b) => (a.versand.am ?? '').localeCompare(b.versand.am ?? ''));
    return karte;
  }, [versandMails]);

  // Zahlen für die Kopfzeile: nur der gezeigte Monat
  const imMonat = (d: Date | null) => !!d && d.getFullYear() === jahr && d.getMonth() === mon;
  const anzahlVersand = versandMails.filter((m) => imMonat(alsTag(m.versand.am))).length;
  const anzahlEvents = mails.filter((m) => {
    const a = alsTag(m.event.beginn);
    if (!a) return false;
    const b = alsTag(m.event.ende) ?? a;
    return a <= new Date(jahr, mon + 1, 0) && b >= new Date(jahr, mon, 1);
  }).length;

  // Agenda (Handy): Tage des Monats mit Einträgen; ein Event steht an seinem ersten Tag im Monat.
  const agenda = useMemo(() => {
    const tage: { tag: Date; events: PlanungsMail[]; marken: PlanungsMail[] }[] = [];
    const letzter = new Date(jahr, mon + 1, 0).getDate();
    for (let i = 1; i <= letzter; i++) {
      const tag = new Date(jahr, mon, i);
      const events = mails.filter((m) => {
        const a = alsTag(m.event.beginn);
        if (!a) return false;
        const b = alsTag(m.event.ende) ?? a;
        const ersterImMonat = a < new Date(jahr, mon, 1) ? new Date(jahr, mon, 1) : a;
        return b >= tag && ersterImMonat.getTime() === tag.getTime();
      });
      const marken = markenJeTag.get(tagIso(tag)) ?? [];
      if (events.length || marken.length) tage.push({ tag, events, marken });
    }
    return tage;
  }, [jahr, mon, mails, markenJeTag]);

  const schliessen = useCallback(() => setOffenerTag(null), []);
  const wechseln = (d: Date) => { setOffenerTag(null); onMonat(d); };
  const istAktuellerMonat = heuteTag.getFullYear() === jahr && heuteTag.getMonth() === mon;

  return (
    <div className="gm-kal">
      <div className="gm-kal-kopf">
        <div className="gm-kal-titel">
          <h2 aria-live="polite">{monatTitel(monat)}</h2>
          <span className="gm-kal-summe">
            {[
              zeige !== 'event' ? `${anzahlVersand} ${anzahlVersand === 1 ? 'Versandtermin' : 'Versandtermine'}` : null,
              zeige !== 'versand' ? `${anzahlEvents} ${anzahlEvents === 1 ? 'Event' : 'Events'}` : null,
            ].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="gm-kal-nav">
          <button type="button" className="btn btn-secondary btn-sm gm-kal-pfeil" onClick={() => wechseln(new Date(jahr, mon - 1, 1))} aria-label="Vorheriger Monat" title="Vorheriger Monat">
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => wechseln(new Date(heuteTag.getFullYear(), heuteTag.getMonth(), 1))} disabled={istAktuellerMonat}>
            Heute
          </button>
          <button type="button" className="btn btn-secondary btn-sm gm-kal-pfeil" onClick={() => wechseln(new Date(jahr, mon + 1, 1))} aria-label="Nächster Monat" title="Nächster Monat">
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Monatsraster (ab 700 px Breite) */}
      <div className="gm-kal-raster" aria-label={`Kalender ${monatTitel(monat)}`} role="group">
        <div className="gm-kal-wochentage" aria-hidden="true">
          {WOCHENTAGE_KURZ.map((w, i) => <span key={w} className={i >= 5 ? 'gm-kal-we' : undefined}>{w}</span>)}
        </div>
        {wochen.map((woche, wi) => {
          const zeilen: CSSProperties = { gridTemplateRows: `2rem${woche.spuren ? ` repeat(${woche.spuren}, 1.5rem)` : ''} minmax(2.75rem, auto)` };
          const markenZeile = woche.spuren + 2;
          return (
            <div key={wi} className="gm-kal-woche" style={zeilen}>
              {woche.tage.map((tag, i) => {
                const iso = tagIso(tag);
                const klassen = ['gm-kal-tag'];
                if (tag.getMonth() !== mon) klassen.push('gm-kal-tag--aussen');
                if (i >= 5) klassen.push('gm-kal-tag--we');
                if (iso === heuteIso) klassen.push('gm-kal-tag--heute');
                return (
                  <div key={iso} className={klassen.join(' ')} style={{ gridColumn: i + 1, gridRow: '1 / -1' }}>
                    <span className="gm-kal-nr" aria-hidden="true">{tag.getDate()}</span>
                    <span className="sr-only">{tagLang(tag)}{iso === heuteIso ? ' (heute)' : ''}</span>
                  </div>
                );
              })}

              {woche.abschnitte.filter((a) => a.spur < MAX_SPUREN).map((a) => (
                <button
                  key={`${a.mail.id}-${wi}`}
                  type="button"
                  className={`gm-kal-event${a.beginntHier ? ' gm-kal-event--start' : ''}${a.endetHier ? ' gm-kal-event--ende' : ''}${rueckKlasse(a.mail, rueckmeldungen, 'gm-kal-event')}`}
                  style={{ gridColumn: `${a.start + 1} / ${a.ende + 2}`, gridRow: a.spur + 2 }}
                  onClick={() => onOeffnen(a.mail)}
                  aria-label={eventLabel(a.mail, rueckmeldungen)}
                  title={eventLabel(a.mail, rueckmeldungen)}
                >
                  {!a.beginntHier && <ChevronLeft size={11} aria-hidden="true" className="gm-kal-weiter" />}
                  <span className="gm-kal-text">{eventTitel(a.mail)}</span>
                  {a.beginntHier && <RueckBadge m={a.mail} rueck={rueckmeldungen} klein />}
                  {!a.endetHier && <ChevronRight size={11} aria-hidden="true" className="gm-kal-weiter" />}
                </button>
              ))}

              {woche.tage.map((tag, i) => {
                const iso = tagIso(tag);
                const marken = markenJeTag.get(iso) ?? [];
                const amTag = woche.abschnitte.filter((a) => a.start <= i && a.ende >= i);
                const eventsSichtbar = amTag.filter((a) => a.spur < MAX_SPUREN).length;
                const platz = Math.max(0, MAX_JE_TAG - eventsSichtbar);
                const zeigen = marken.slice(0, platz);
                const mehr = (amTag.length - eventsSichtbar) + (marken.length - zeigen.length);
                if (marken.length === 0 && mehr === 0) return null;
                return (
                  <div key={`m-${iso}`} className={`gm-kal-marken${offenerTag === iso ? ' gm-kal-marken--offen' : ''}`} style={{ gridColumn: i + 1, gridRow: markenZeile }}>
                    {zeigen.map((m) => <Marke key={m.id} m={m} onOeffnen={onOeffnen} rueck={rueckmeldungen} />)}
                    {mehr > 0 && (
                      <button
                        type="button"
                        className="gm-kal-mehr"
                        onClick={() => setOffenerTag(offenerTag === iso ? null : iso)}
                        aria-expanded={offenerTag === iso}
                        aria-label={`${mehr} weitere am ${tagLang(tag)} zeigen`}
                      >
                        +{mehr} mehr
                      </button>
                    )}
                    {offenerTag === iso && (
                      <TagesPopover
                        tag={tag}
                        events={amTag.map((a) => a.mail)}
                        marken={marken}
                        rechts={i >= 4}
                        rueck={rueckmeldungen}
                        onOeffnen={(m) => { schliessen(); onOeffnen(m); }}
                        onClose={schliessen}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Agenda (Handy) */}
      <div className="gm-kal-agenda">
        {agenda.length === 0 ? (
          <p className="gm-kal-agenda-leer">Im {MONATSNAMEN[mon]} ist nichts geplant.</p>
        ) : (
          <ol className="gm-kal-agenda-liste">
            {agenda.map(({ tag, events, marken }) => {
              const iso = tagIso(tag);
              return (
                <li key={iso} className={`gm-kal-agenda-tag${iso === heuteIso ? ' gm-kal-agenda-tag--heute' : ''}`}>
                  <h3 className="gm-kal-agenda-datum">
                    <span className="gm-kal-agenda-wt" aria-hidden="true">{WOCHENTAG_LANG[tag.getDay()].slice(0, 2)}</span>
                    <span className="gm-kal-agenda-tt" aria-hidden="true">{String(tag.getDate()).padStart(2, '0')}.{String(tag.getMonth() + 1).padStart(2, '0')}.</span>
                    <span className="sr-only">{tagLang(tag)}</span>
                    {iso === heuteIso && <span className="badge badge-info">heute</span>}
                  </h3>
                  <div className="gm-kal-agenda-eintraege">
                    {events.map((m) => <EventEintrag key={`e-${m.id}`} m={m} onOeffnen={onOeffnen} rueck={rueckmeldungen} />)}
                    {marken.map((m) => <Marke key={`v-${m.id}`} m={m} onOeffnen={onOeffnen} rueck={rueckmeldungen} lang />)}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <ul className="gm-kal-legende" aria-label="Legende">
        {zeige !== 'event' && (
          <>
            <li><span className="gm-kal-probe gm-kal-probe--eingeplant" aria-hidden="true" />In Brevo eingeplant</li>
            <li><span className="gm-kal-probe gm-kal-probe--angehalten" aria-hidden="true" />In Brevo angehalten</li>
            <li><span className="gm-kal-probe gm-kal-probe--geplant" aria-hidden="true" />Geplant oder Wunschtermin</li>
            <li><span className="gm-kal-probe gm-kal-probe--versendet" aria-hidden="true" />Versendet</li>
          </>
        )}
        {zeige !== 'versand' && <li><span className="gm-kal-probe gm-kal-probe--event" aria-hidden="true" />Event</li>}
        {rueckmeldungen && (
          <>
            <li><span className="gm-kal-rueck gm-kal-rueck--neu" aria-hidden="true"><MessageSquare size={10} />2</span>Neue Rückmeldung, noch ungelesen</li>
            <li><span className="gm-kal-rueck gm-kal-rueck--offen" aria-hidden="true"><MessageSquare size={10} />1</span>Offene Rückmeldung einarbeiten</li>
          </>
        )}
      </ul>
    </div>
  );
}
